import {useCallback, useMemo, useRef, useState} from 'react';
import {Check, Info, Pen, Wand} from 'lucide-react';
import {Button, LogoImage, RichTextEditor, ScrollArea, Skeleton} from '@tinct/web-components';
import {cn} from '@tinct/core-utils';
import {Company, EmbeddedBlock, EmbeddedBlockVariant, EmbeddedPage, ReadBuyer} from '@tinct/core-api-models';
import {useGetBuyers, usePatchBlockVariant} from '@tinct/core-api';
import {getBuyerFillLogoUrl, getBuyerLogoUrl} from '@tinct/web-feature-companies';
import {isLightHexColor} from '../../../utils/is-light-hex-color';
import {useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {usePublishedEditNotice} from "../hooks";
import {ChangesNotLiveModal} from "../../../modals/changes-not-live-modal/changes-not-live-modal";

type EditNotice = () => void;

// -- Types --

type PreviewSidebarProps = {
  block: EmbeddedBlock;
  page: EmbeddedPage;
  variant: EmbeddedBlockVariant;
  /** Max logos selectable for a `customer_logo` block. */
  maxLogos?: number;
};

// -- Personalized Block --

function PersonalizedBlock({
  pageId,
  blockVariantId,
  personalizedValue,
  personalizedValueAsText,
  notify,
}: {
  pageId: string;
  blockVariantId: string;
  personalizedValue: string;
  personalizedValueAsText: string;
  notify: EditNotice;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const editValueRef = useRef(personalizedValue);
  const { mutate, isPending } = usePatchBlockVariant();

  const handleChange = (value: string) => {
    editValueRef.current = value;
  };

  // Save immediately, then inform the user the edited page needs republishing.
  const handleSave = () => {
    mutate(
      { pageId, blockVariantId, data: { value: editValueRef.current } },
      {
        onSuccess: () => {
          setIsEditing(false);
          notify();
        },
      },
    );
  };

  const handleEdit = () => {
    editValueRef.current = personalizedValue;
    setIsEditing(true);
  };

  return (
    <div className="flex flex-col gap-2 rounded-2xl bg-indigo-50 p-4">
      <div className="flex items-center gap-2.5 pt-[3px]">
        <span className="text-sm font-medium leading-none text-foreground">
          Personalized
        </span>
      </div>

      {isEditing ? (
        <>
          <div className="flex flex-col rounded-md border border-input bg-background shadow-xs">
            <RichTextEditor
              className="min-h-16 resize-none border-0 shadow-none focus-visible:ring-0"
              value={personalizedValue}
              onChange={handleChange}
              placeholder="AI-generated content..."
              actions={[
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7"
                  isLoading={isPending}
                  onClick={handleSave}
                >
                  Save
                </Button>
              ]}
            />
          </div>
          {/* Unlike a chat edit, a manual edit isn't turned into a campaign rule. */}
          <p className="flex items-start gap-1.5 text-xs leading-4 text-muted-foreground">
            <Info className="mt-0.5 size-3 shrink-0" />
            <span>
              This edit only changes this page: Tinct won&rsquo;t learn from it, and
              future generations won&rsquo;t pick it up. Ask Tinct in the chat to make it a rule.
            </span>
          </p>
        </>
      ) : (
        <>
          <p className="text-sm leading-5 text-muted-foreground">
            {personalizedValueAsText || 'No content'}
          </p>
          <div className="flex flex-col items-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEdit}
            >
              <Pen className="size-4" />
              Edit
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// -- Description Tab --

const SidebarGroupLabel = ({
  label,
  action,
}: {
  label: string;
  action?: React.ReactNode;
}) => (
  <div className="flex h-8 items-center justify-between px-2">
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    {action}
  </div>
);

function DescriptionTab({
  block,
  variant,
  pageId,
  blockVariantId,
  notify,
}: {
  block: EmbeddedBlock | null;
  variant: EmbeddedBlockVariant | null;
  pageId: string;
  blockVariantId: string;
  notify: EditNotice;
}) {
  if (!block) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-sm text-muted-foreground">
          Select a block to view details
        </p>
      </div>
    );
  }

  const originalValue = block.valueAsText ?? '';
  const personalizedValue = variant?.value ?? '';
  const personalizedValueAsText = variant?.valueAsText ?? '';

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-4 p-4">
        {/* Instructions */}
        <div className="flex flex-col">
          <SidebarGroupLabel label="Instructions" />
          <div className="flex flex-col gap-2 rounded-2xl bg-secondary p-4">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium leading-none text-foreground">
                What Tinct understood
              </span>
              <Info className="size-3 text-foreground" />
            </div>
            <p className="text-sm leading-5 text-muted-foreground">
              {block.label ?? 'No description available for this block.'}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col">
          <SidebarGroupLabel
            label="Content"
            action={
              <button className="flex size-5 items-center justify-center rounded-md opacity-70">
                <Wand className="size-4" />
              </button>
            }
          />
          <div className="flex flex-col gap-2">
            {/* Original */}
            <div className="flex flex-col gap-2 rounded-2xl bg-secondary p-4">
              <div className="flex items-center gap-2.5 pt-[3px]">
                <span className="text-sm font-medium leading-none text-foreground">
                  Original
                </span>
              </div>
              <p className="text-sm leading-5 text-muted-foreground">
                {originalValue || 'No content'}
              </p>
            </div>

            {/* Personalized with AI */}
            <PersonalizedBlock
              pageId={pageId}
              blockVariantId={blockVariantId}
              personalizedValue={personalizedValue}
              personalizedValueAsText={personalizedValueAsText}
              notify={notify}
            />
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

// -- Social Proof Logos Content --

function LogoSelectCard({
  buyer,
  greyscale = false,
  dark = false,
  fillColor = null,
  selected,
  error,
  disabled,
  onToggle,
}: {
  buyer: ReadBuyer;
  /** Which logo variant to resolve — mirrors the block's display settings. */
  greyscale?: boolean;
  dark?: boolean;
  fillColor?: string | null;
  selected: boolean;
  /** Selected company that no longer exists in the buyer pool. */
  error?: boolean;
  /** At the max-selection cap and not selected → can't be added. */
  disabled?: boolean;
  onToggle: () => void;
}) {
  // Same variant resolution as the editor's LogoCard: with a fill color any
  // base variant works; otherwise the buyer's own greyscale/dark combination
  // wins, falling back to the company's.
  const logoUrl = fillColor
    ? getBuyerFillLogoUrl(buyer)
    : getBuyerLogoUrl(buyer, { greyscale, dark });
  const name = buyer.company?.name ?? '';
  // Unselected and still under the cap → can be added (shows an empty checkbox
  // on hover). Distinct from the disabled (cap-reached) state, which is dimmed.
  const selectable = !selected && !disabled;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'group relative flex aspect-square flex-col rounded-md border px-2 pt-2 pb-1 text-left transition-colors',
        error
          ? 'border-destructive bg-destructive/10'
          : 'border-border bg-background',
        selectable && 'hover:bg-neutral-50',
        !selected && disabled && 'opacity-40',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      )}
    >
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pt-2">
        {logoUrl ? (
          <LogoImage
            src={logoUrl}
            alt={name}
            fillColor={fillColor}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <span className="text-sm font-medium text-muted-foreground">
            {name.charAt(0).toUpperCase()}
          </span>
        )}

        {/* Selected → filled check; selectable → empty box on hover. */}
        {selected ? (
          <span
            className={cn(
              'absolute right-0 top-0 flex size-4 items-center justify-center rounded-[4px] shadow-xs',
              error
                ? 'bg-destructive text-white'
                : 'bg-primary text-primary-foreground'
            )}
          >
            <Check className="size-3.5" />
          </span>
        ) : (
          selectable && (
            <span className="absolute right-0 top-0 hidden size-4 rounded-[4px] border border-input bg-background shadow-xs group-hover:block" />
          )
        )}
      </div>
      <p
        className={cn(
          'w-full truncate text-center text-xs font-medium leading-[18px]',
          error
            ? 'text-destructive'
            : disabled
              ? 'text-muted-foreground'
              : 'text-foreground'
        )}
      >
        {name}
      </p>
    </button>
  );
}

/**
 * Logos-banner content shown when the selected block is `customer_logo`.
 * Lists every organization buyer; a buyer is "selected" when its company id is
 * in the variant's `companies`. Selecting/unselecting patches the variant.
 * Up to `maxLogos` can be selected. Selected companies that are no longer in
 * the buyer pool are surfaced as error cards (still unselectable).
 */
function LogosContent({
  pageId,
  blockVariantId,
  variant,
  maxLogos,
  greyscale = false,
  dark = false,
  fillColor = null,
  notify,
}: {
  pageId: string;
  blockVariantId: string;
  variant: EmbeddedBlockVariant;
  maxLogos: number;
  /** The block's display settings — pick which logo variant the cards show. */
  greyscale?: boolean;
  dark?: boolean;
  fillColor?: string | null;
  notify: EditNotice;
}) {
  const workspaceId = useGetCurrentWorkspaceId();
  const { data, isLoading } = useGetBuyers({
    offset: 0,
    limit: 100,
    filters: { organizationId: workspaceId },
    fields: {
      id: null,
      brandmarkLogo: null,
      brandmarkLogoGreyscale: null,
      brandmarkLogoDark: null,
      brandmarkLogoGreyscaleDark: null,
      company: {
        fields: {
          id: null,
          name: null,
          brandmarkLogo: null,
          brandmarkLogoGreyscale: null,
          brandmarkLogoDark: null,
          brandmarkLogoGreyscaleDark: null,
        }
      }
    }
  });
  const buyers = data?.data ?? [];

  const { mutate: patchVariant, isPending } = usePatchBlockVariant();

  const selectedCompanies = useMemo(
    () => variant.companies ?? [],
    [variant.companies]
  );
  const selectedIds = useMemo(
    () => new Set(selectedCompanies.map((c) => c.id)),
    [selectedCompanies]
  );
  const atMax = selectedIds.size >= maxLogos;

  const handleToggle = useCallback(
    (companyId: string) => {
      const isSelected = selectedIds.has(companyId);
      // Can't add beyond the cap; unselecting is always allowed.
      if (!isSelected && selectedIds.size >= maxLogos) return;

      const nextIds = new Set(selectedIds);
      if (isSelected) nextIds.delete(companyId);
      else nextIds.add(companyId);

      patchVariant(
        {
          pageId,
          blockVariantId,
          data: { companies: [...nextIds].map((id) => ({ id })) },
        },
        { onSuccess: () => notify() },
      );
    },
    [selectedIds, maxLogos, patchVariant, pageId, blockVariantId, notify]
  );

  // Buyer pool, plus any selected company that's no longer a buyer (error).
  const poolBuyers = buyers.filter((b): b is ReadBuyer & { company: Company } => !!b.company);
  const buyerIds = new Set(poolBuyers.map((b) => b.company.id));
  // Orphans have no ReadBuyer anymore; wrap the company so logo resolution
  // goes through the same buyer → company variant fallback.
  const orphanBuyers = selectedCompanies
    .filter((c) => !buyerIds.has(c.id))
    .map((company) => ({ company }) as ReadBuyer);

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-4 p-4">
        <p className="text-base font-medium leading-normal text-foreground">
          Logos banner
        </p>

        <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4">
          <p className="text-sm leading-5 text-indigo-600">
            Tinct replaces your default logo block with its own widget. This
            allows the AI to dynamically personalize the logos displayed based
            on each visitor's industry and profile.
          </p>
        </div>

        <div className="flex flex-col">
          <SidebarGroupLabel
            label={`Content (${selectedIds.size}/${maxLogos})`}
          />
          {isLoading ? (
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-md" />
              ))}
            </div>
          ) : (
            <div className={cn("grid grid-cols-3 gap-2", {
              // Light fill colors need the dark card background to be visible.
              'dark': dark || (!!fillColor && isLightHexColor(fillColor))
            })}>
              {poolBuyers.map((buyer) => {
                const selected = selectedIds.has(buyer.company.id);
                return (
                  <LogoSelectCard
                    key={buyer.company.id}
                    buyer={buyer}
                    greyscale={greyscale}
                    dark={dark}
                    fillColor={fillColor}
                    selected={selected}
                    disabled={isPending || (!selected && atMax)}
                    onToggle={() => handleToggle(buyer.company.id)}
                  />
                );
              })}
              {orphanBuyers.map((buyer) => (
                <LogoSelectCard
                  key={buyer.company?.id}
                  buyer={buyer}
                  greyscale={greyscale}
                  dark={dark}
                  fillColor={fillColor}
                  selected
                  error
                  disabled={isPending}
                  onToggle={() => buyer.company && handleToggle(buyer.company.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}

// -- Main Sidebar --

export const PreviewSidebar = ({
  block,
  page,
  variant,
  maxLogos = 8,
}: PreviewSidebarProps) => {
  // Editing a block on a published page marks it outdated. Save runs
  // immediately; afterwards we inform the user the page needs republishing —
  // but only when it was still in sync (no point telling them about a page
  // that is already outdated). Kept above the early return below so the hook
  // order stays stable across blocks.
  const isInSync = !page.outdatedStatus || page.outdatedStatus === 'none';
  const { notify, modalProps } = usePublishedEditNotice(
    page.status === 'published' && isInSync,
  );

  // The chat moved to the studio's persistent conversation column, so the
  // sidebar is down to the block's details: description, or the logos banner.
  const content =
    block.type === 'customer_logo' ? (
      <LogosContent
        pageId={page.id as string}
        blockVariantId={variant.id as string}
        variant={variant}
        maxLogos={maxLogos}
        greyscale={block.greyscale ?? false}
        dark={block.dark ?? false}
        fillColor={block.logoFillColor ?? null}
        notify={notify}
      />
    ) : (
      <div className="min-h-0 flex-1 overflow-hidden pt-4">
        <DescriptionTab
          block={block}
          variant={variant}
          pageId={page.id as string}
          blockVariantId={variant.id as string}
          notify={notify}
        />
      </div>
    );

  return (
    <>
      {content}
      {/* Sibling of the content above: the logos banner renders its own
          content, so a notice nested in the description would never show for
          a logo edit. */}
      <ChangesNotLiveModal
        {...modalProps}
        step="some variant content"
        alertDescription="The edited variant pages need to be published."
        callToAction="Generate and publish the new version of your variant pages."
        actions={[
          { label: 'Ok noted !', variant: 'outline', onClick: () => modalProps.onOpenChange(false) },
        ]}
      />
    </>
  );
};
